import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import Breadcrumb from '../components/Breadcrumb';
import {
  Users,
  FileText,
  BarChart3,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  ArrowLeft,
  Shield,
  Loader2,
  Plus,
  Edit,
  Eye,
  Settings2,
  Globe,
  BookOpen,
  Palette,
  AlertTriangle,
  TrendingUp,
  Activity,
  LayoutDashboard,
  ChevronRight,
  ExternalLink,
  Crown,
  Calendar,
  Save
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(true);
  const [membershipModal, setMembershipModal] = useState(null); // user object or null
  const [membershipForm, setMembershipForm] = useState({
    membership_type: '',
    subscription_end: '',
    subscription_status: 'active'
  });
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [savingMembership, setSavingMembership] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user === null && token) return;
    setAuthChecked(true);
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, token, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, listingsRes, mtRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/listings`, { headers }),
        axios.get(`${API}/settings/membership-types`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setListings(listingsRes.data);
      setMembershipTypes(mtRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = { pending: t('admin.pending', 'Pending'), approved: t('admin.approved', 'Approved'), rejected: t('admin.rejected', 'Rejected') };

  const updateListingStatus = async (listingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/listings/${listingId}/status?new_status=${newStatus}`, {}, { headers });
      toast({ title: statusLabels[newStatus], description: t('admin.statusUpdated', 'Status updated') });
      fetchData();
    } catch (error) {
      toast({ title: t('common.error', 'Error'), description: t('admin.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    }
  };

  const deleteListing = async (listingId) => {
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`, { headers });
      toast({ title: t('common.success', 'Success'), description: t('listing.listingDeleted', 'Listing deleted') });
      fetchData();
    } catch (error) {
      toast({ title: t('common.error', 'Error'), description: t('admin.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    }
  };

  const toggleListingAvailability = async (listingId, currentAvailable) => {
    try {
      await axios.put(`${API}/listings/${listingId}`, { available: !currentAvailable }, { headers });
      toast({ title: t('common.success', 'Success'), description: !currentAvailable ? t('admin.listingActivated', 'Listing activated') : t('admin.listingDeactivated', 'Listing deactivated') });
      fetchData();
    } catch (error) {
      toast({ title: t('common.error', 'Error'), description: t('admin.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast({ title: t('common.success', 'Success'), description: t('admin.userDeleted', 'User deleted') });
      fetchData();
    } catch (error) {
      toast({ title: t('common.error', 'Error'), description: t('admin.somethingWentWrong', 'Something went wrong'), variant: 'destructive' });
    }
  };

  const openMembershipModal = (u) => {
    setMembershipForm({
      membership_type: u.membership_type || 'normal',
      subscription_end: u.subscription_end ? new Date(u.subscription_end).toISOString().split('T')[0] : '',
      subscription_status: u.subscription_status || 'inactive'
    });
    setMembershipModal(u);
  };

  const saveMembership = async () => {
    if (!membershipModal) return;
    try {
      setSavingMembership(true);
      await axios.put(`${API}/admin/users/${membershipModal.id}/membership`, {
        membership_type: membershipForm.membership_type,
        subscription_end: membershipForm.subscription_end,
        subscription_status: membershipForm.subscription_status
      }, { headers });
      toast({ title: t('common.success', 'Success'), description: t('admin.membershipUpdated', 'Membership updated') });
      setMembershipModal(null);
      fetchData();
    } catch (error) {
      toast({ title: t('common.error', 'Error'), description: t('admin.membershipUpdateError', 'Failed to update membership'), variant: 'destructive' });
    } finally {
      setSavingMembership(false);
    }
  };

  const getSubscriptionBadge = (u) => {
    if (!u.subscription_end) return { color: 'bg-zinc-800 text-zinc-400 border-zinc-700', label: t('admin.undefined', 'Undefined') };
    const now = new Date();
    const end = new Date(u.subscription_end);
    if (u.subscription_status === 'active' && end > now) {
      return { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: t('common.active', 'Active') };
    }
    if (u.subscription_status === 'pending_payment') {
      return { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', label: t('membership.subscriptionPending', 'Payment Pending') };
    }
    return { color: 'bg-red-500/10 text-red-400 border-red-500/20', label: t('admin.expired', 'Expired') };
  };

  const pendingListings = listings.filter(l => l.status === 'pending');
  const pendingCount = pendingListings.length;
  const approvedCount = listings.filter(l => l.status === 'approved').length;

  const filteredListings = listings.filter(listing => {
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    const matchesSearch = !searchTerm ||
      listing.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredUsers = users.filter(u =>
    !searchTerm ||
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!authChecked || loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <p className="text-zinc-500 text-sm">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
    approved: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
    rejected: 'bg-red-500/10 text-red-400 border border-red-500/20'
  };

  const sidebarSections = [
    {
      title: t('admin.general', 'General'),
      items: [
        { id: 'dashboard', icon: LayoutDashboard, label: t('admin.dashboard', 'Dashboard'), color: 'text-emerald-400' },
        { id: 'pending', icon: Clock, label: t('admin.pendingApprovals', 'Pending Approvals'), color: 'text-amber-400', badge: pendingCount },
        { id: 'listings', icon: FileText, label: t('listing.allListings', 'All Listings'), color: 'text-blue-400' },
        { id: 'users', icon: Users, label: t('admin.users', 'Users'), color: 'text-purple-400' },
      ]
    },
    {
      title: t('admin.management', 'Management'),
      items: [
        { id: 'settings', icon: Settings2, label: t('admin.siteSettings', 'Site Settings'), link: '/admin/settings' },
        { id: 'blog', icon: BookOpen, label: t('blog.blogManagement', 'Blog Management'), link: '/admin/blog' },
        { id: 'seo', icon: Globe, label: t('seo.seoManagement', 'SEO Management'), link: '/admin/seo' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: t('admin.adminPanel', 'Admin Panel'), href: null }]} />

      {/* Top Header */}
      <header className="bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-800/50 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 md:px-6 h-14">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-zinc-400 hover:text-white transition-colors">
              <LayoutDashboard className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-sm font-semibold text-white leading-none">{t('admin.adminPanel', 'Admin Panel')}</h1>
                <p className="text-[11px] text-zinc-500 mt-0.5">{user.email}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white h-8 text-xs"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              {t('admin.viewSite', 'View Site')}
            </Button>
            <Button
              onClick={() => navigate('/create-listing')}
              className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
              size="sm"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {t('admin.newListing', 'New Listing')}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-60 min-h-[calc(100vh-3.5rem)] bg-zinc-900/40 border-r border-zinc-800/50 p-4 md:sticky md:top-14 md:self-start`}>
          {sidebarSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-6' : ''}>
              <p className="text-[10px] font-semibold text-zinc-600 uppercase tracking-widest px-3 mb-2">
                {section.title}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.link) { navigate(item.link); }
                        else { setActiveSection(item.id); setSearchTerm(''); }
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all text-sm ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-400' : ''}`} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="bg-amber-500 text-black text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                      {item.link && <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-4 md:p-6 min-w-0 ${mobileMenuOpen ? 'hidden md:block' : 'block'}`}>
          {/* === DASHBOARD === */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label={t('admin.totalListings', 'Total Listings')}
                  value={stats?.total_listings || 0}
                  icon={<FileText className="w-5 h-5" />}
                  color="blue"
                />
                <StatCard
                  label={t('admin.totalUsers', 'Total Users')}
                  value={stats?.total_users || 0}
                  icon={<Users className="w-5 h-5" />}
                  color="purple"
                />
                <StatCard
                  label={t('listing.publishedStatus', 'Published')}
                  value={approvedCount}
                  icon={<CheckCircle className="w-5 h-5" />}
                  color="emerald"
                />
                <StatCard
                  label={t('admin.pendingListings', 'Pending')}
                  value={pendingCount}
                  icon={<Clock className="w-5 h-5" />}
                  color="amber"
                  onClick={() => setActiveSection('pending')}
                />
              </div>

              {/* Quick Actions & Recent */}
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Quick Actions */}
                <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-5">
                  <h3 className="text-sm font-semibold text-white mb-4">{t('admin.quickActions', 'Quick Actions')}</h3>
                  <div className="space-y-2">
                    <QuickAction label={t('admin.siteSettings', 'Site Settings')} icon={<Settings2 className="w-4 h-4" />} onClick={() => navigate('/admin/settings')} />
                    <QuickAction label={t('blog.blogManagement', 'Blog Management')} icon={<BookOpen className="w-4 h-4" />} onClick={() => navigate('/admin/blog')} />
                    <QuickAction label={t('seo.seoSettings', 'SEO Settings')} icon={<Globe className="w-4 h-4" />} onClick={() => navigate('/admin/seo')} />
                    <QuickAction label={t('admin.addListing', 'Add Listing')} icon={<Plus className="w-4 h-4" />} onClick={() => navigate('/create-listing')} />
                  </div>
                </div>

                {/* Pending Approvals Preview */}
                <div className="lg:col-span-2 bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-white">{t('admin.recentPending', 'Recent Pending')}</h3>
                    {pendingCount > 0 && (
                      <button onClick={() => setActiveSection('pending')} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                        {t('common.browseAll', 'View All')} ({pendingCount})
                      </button>
                    )}
                  </div>
                  {pendingCount === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-10 h-10 text-emerald-500/30 mx-auto mb-2" />
                      <p className="text-zinc-500 text-sm">{t('admin.noPendingListings', 'No pending listings')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {pendingListings.slice(0, 4).map((listing) => (
                        <div key={listing.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors">
                          <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                            {listing.images?.[0] ? (
                              <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-bold">{listing.name.charAt(0)}</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{listing.name}</p>
                            <p className="text-zinc-500 text-xs truncate">{listing.title}</p>
                          </div>
                          <div className="flex gap-1.5">
                            <button onClick={() => updateListingStatus(listing.id, 'approved')} className="w-7 h-7 rounded-md bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex items-center justify-center transition-colors" title={t('admin.approve', 'Approve')}>
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => updateListingStatus(listing.id, 'rejected')} className="w-7 h-7 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center transition-colors" title={t('admin.reject', 'Reject')}>
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Users */}
              <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">{t('admin.recentUsers', 'Recent Users')}</h3>
                  <button onClick={() => setActiveSection('users')} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                    {t('common.browseAll', 'View All')}
                  </button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {users.slice(0, 6).map((u) => (
                    <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-white text-xs font-bold">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{u.name}</p>
                        <p className="text-zinc-500 text-xs truncate">{u.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* === PENDING APPROVALS === */}
          {activeSection === 'pending' && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">{t('admin.pendingApprovals', 'Pending Approvals')}</h2>
                  <p className="text-zinc-500 text-xs">{t('admin.listingsAwaitingApproval', '{{count}} listings awaiting approval', { count: pendingCount })}</p>
                </div>
              </div>

              {pendingCount === 0 ? (
                <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-16 text-center">
                  <CheckCircle className="w-14 h-14 text-emerald-500/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">{t('admin.allApproved', 'All Approved')}</h3>
                  <p className="text-zinc-500 text-sm">{t('admin.noPendingListings', 'No pending listings')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingListings.map((listing) => (
                    <div key={listing.id} className="bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-4 flex flex-col md:flex-row items-start md:items-center gap-4 hover:border-zinc-700/50 transition-colors">
                      <div className="w-14 h-14 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0">
                        {listing.images?.[0] ? (
                          <img src={listing.images[0]} alt={listing.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-lg font-bold">{listing.name.charAt(0)}</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{listing.name}</h3>
                        <p className="text-zinc-400 text-sm truncate">{listing.title}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{listing.city}</span>
                          <span>{listing.category}</span>
                          <span>{new Date(listing.created_at).toLocaleDateString('en-US')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/escort/${listing.slug || listing.id}`)} className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 h-8 text-xs">
                          <Eye className="w-3.5 h-3.5 mr-1" />{t('common.view', 'View')}
                        </Button>
                        <Button size="sm" onClick={() => updateListingStatus(listing.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />{t('admin.approve', 'Approve')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateListingStatus(listing.id, 'rejected')} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs">
                          <XCircle className="w-3.5 h-3.5 mr-1" />{t('admin.reject', 'Reject')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === ALL LISTINGS === */}
          {activeSection === 'listings' && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">{t('listing.allListings', 'All Listings')}</h2>
                  <p className="text-zinc-500 text-xs">{t('admin.registeredListings', '{{count}} registered listings', { count: listings.length })}</p>
                </div>
              </div>

              <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50">
                {/* Filters */}
                <div className="p-4 border-b border-zinc-800/50 flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input placeholder={t('admin.searchListings', 'Search listings...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-9 text-sm" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-40 bg-zinc-800/50 border-zinc-700/50 text-zinc-300 h-9 text-sm">
                      <SelectValue placeholder={t('common.status', 'Status')} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="all" className="text-zinc-300">{t('admin.allStatuses', 'All Statuses')}</SelectItem>
                      <SelectItem value="pending" className="text-zinc-300">{t('admin.pending', 'Pending')}</SelectItem>
                      <SelectItem value="approved" className="text-zinc-300">{t('admin.approved', 'Approved')}</SelectItem>
                      <SelectItem value="rejected" className="text-zinc-300">{t('admin.rejected', 'Rejected')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800/50">
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('admin.listings', 'Listing')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('listing.city', 'City')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('common.status', 'Status')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('common.active', 'Active')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredListings.map((listing) => (
                        <tr key={listing.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                                {listing.images?.[0] ? (
                                  <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-bold">{listing.name.charAt(0)}</div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate">{listing.name}</p>
                                <p className="text-zinc-500 text-xs truncate">{listing.title}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">{listing.city}</td>
                          <td className="py-3 px-4">
                            <Badge className={`text-[11px] ${statusColors[listing.status]}`}>
                              {statusLabels[listing.status]}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Switch checked={listing.available} onCheckedChange={() => toggleListingAvailability(listing.id, listing.available)} className="data-[state=checked]:bg-emerald-500" />
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-0.5">
                              <IconBtn icon={<Eye className="w-3.5 h-3.5" />} color="text-zinc-400 hover:text-blue-400" title={t('common.view', 'View')} onClick={() => navigate(`/escort/${listing.slug || listing.id}`)} />
                              <IconBtn icon={<Edit className="w-3.5 h-3.5" />} color="text-zinc-400 hover:text-yellow-400" title={t('common.edit', 'Edit')} onClick={() => navigate(`/edit-listing/${listing.id}`)} />
                              {listing.status !== 'approved' && <IconBtn icon={<CheckCircle className="w-3.5 h-3.5" />} color="text-zinc-400 hover:text-emerald-400" title={t('admin.approve', 'Approve')} onClick={() => updateListingStatus(listing.id, 'approved')} />}
                              {listing.status !== 'rejected' && <IconBtn icon={<XCircle className="w-3.5 h-3.5" />} color="text-zinc-400 hover:text-orange-400" title={t('admin.reject', 'Reject')} onClick={() => updateListingStatus(listing.id, 'rejected')} />}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-zinc-800/50 transition-colors" title={t('common.delete', 'Delete')}><Trash2 className="w-3.5 h-3.5" /></button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">{t('listing.deleteListing', 'Delete Listing')}</AlertDialogTitle>
                                    <AlertDialogDescription className="text-zinc-400">{t('admin.confirmDeleteListing', 'Are you sure you want to delete this listing? This action cannot be undone.')}</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteListing(listing.id)}>{t('common.delete', 'Delete')}</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredListings.length === 0 && <p className="text-center py-10 text-zinc-500 text-sm">{t('admin.noListingsFound', 'No listings found.')}</p>}
                </div>
              </div>
            </div>
          )}

          {/* === USERS === */}
          {activeSection === 'users' && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">{t('admin.users', 'Users')}</h2>
                  <p className="text-zinc-500 text-xs">{t('admin.registeredUsers', '{{count}} registered users', { count: users.length })}</p>
                </div>
              </div>

              <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/50">
                <div className="p-4 border-b border-zinc-800/50">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input placeholder={t('admin.searchUsers', 'Search users...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-800/50 border-zinc-700/50 text-white h-9 text-sm" />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800/50">
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('admin.user', 'User')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('auth.email', 'Email')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('admin.role', 'Role')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('admin.membership', 'Membership')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('common.status', 'Status')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('admin.expiryDate', 'Expiry Date')}</th>
                        <th className="text-left py-3 px-4 text-zinc-500 font-medium text-xs uppercase tracking-wider">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => {
                        const subBadge = getSubscriptionBadge(u);
                        const mtInfo = membershipTypes.find(mt => mt.id === u.membership_type);
                        return (
                        <tr key={u.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-white text-xs font-bold">
                                {u.name.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-white text-sm">{u.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">{u.email}</td>
                          <td className="py-3 px-4">
                            <Badge className={`text-[11px] ${u.role === 'admin' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
                              {u.role === 'admin' ? 'Admin' : t('admin.user', 'User')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className="text-[11px] bg-blue-500/10 text-blue-400 border border-blue-500/20">
                              {mtInfo?.title || u.membership_type || '-'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`text-[11px] border ${subBadge.color}`}>
                              {subBadge.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-zinc-400 text-sm">
                            {u.subscription_end ? new Date(u.subscription_end).toLocaleDateString('en-US') : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              {u.role !== 'admin' && (
                                <>
                                  <button
                                    onClick={() => openMembershipModal(u)}
                                    className="p-1.5 rounded-md text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800/50 transition-colors"
                                    title={t('admin.editMembership', 'Edit Membership')}
                                  >
                                    <Crown className="w-3.5 h-3.5" />
                                  </button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-zinc-800/50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                                      <AlertDialogHeader>
                                        <AlertDialogTitle className="text-white">{t('admin.deleteUser', 'Delete User')}</AlertDialogTitle>
                                        <AlertDialogDescription className="text-zinc-400">{t('admin.confirmDeleteUserDesc', 'Are you sure you want to delete this user? All their listings will also be deleted.')}</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteUser(u.id)}>{t('common.delete', 'Delete')}</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && <p className="text-center py-10 text-zinc-500 text-sm">{t('admin.noUsers', 'No users found.')}</p>}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Membership Edit Modal */}
      <Dialog open={!!membershipModal} onOpenChange={(open) => { if (!open) setMembershipModal(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Crown className="w-5 h-5 text-emerald-400" />
              {t('admin.editMembership', 'Edit Membership')}
            </DialogTitle>
          </DialogHeader>

          {membershipModal && (
            <div className="space-y-5 mt-2">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-white font-bold">
                  {membershipModal.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-medium">{membershipModal.name}</p>
                  <p className="text-zinc-400 text-xs">{membershipModal.email}</p>
                </div>
              </div>

              {/* Membership Type */}
              <div>
                <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.membershipType', 'Membership Type')}</Label>
                <select
                  value={membershipForm.membership_type}
                  onChange={(e) => setMembershipForm(prev => ({ ...prev, membership_type: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3 text-sm"
                >
                  {membershipTypes.map(mt => (
                    <option key={mt.id} value={mt.id}>
                      {mt.title} {mt.is_free !== false ? `(${t('membership.free', 'Free')})` : `(${mt.price}${mt.currency || '₺'}/ay)`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subscription End Date */}
              <div>
                <Label className="text-zinc-400 text-xs mb-2 block flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {t('admin.subscriptionEndDate', 'Subscription End Date')}
                </Label>
                <Input
                  type="date"
                  value={membershipForm.subscription_end}
                  onChange={(e) => setMembershipForm(prev => ({ ...prev, subscription_end: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white h-10"
                />
                {/* Quick date buttons */}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[
                    { label: '+7 Days', days: 7 },
                    { label: '+1 Month', days: 30 },
                    { label: '+3 Months', days: 90 },
                    { label: '+6 Months', days: 180 },
                    { label: '+1 Year', days: 365 },
                  ].map(({ label, days }) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => {
                        const base = membershipForm.subscription_end ? new Date(membershipForm.subscription_end) : new Date();
                        base.setDate(base.getDate() + days);
                        setMembershipForm(prev => ({ ...prev, subscription_end: base.toISOString().split('T')[0] }));
                      }}
                      className="px-2.5 py-1 rounded-md text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subscription Status */}
              <div>
                <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.subscriptionStatus', 'Subscription Status')}</Label>
                <select
                  value={membershipForm.subscription_status}
                  onChange={(e) => setMembershipForm(prev => ({ ...prev, subscription_status: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="pending_payment">Payment Pending</option>
                  <option value="expired">Expired</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Save Button */}
              <Button
                onClick={saveMembership}
                disabled={savingMembership || !membershipForm.subscription_end}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {savingMembership ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {savingMembership ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color, onClick }) => {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/10',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/10',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/10',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/10',
  };
  const iconColors = {
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
  };
  return (
    <div onClick={onClick} className={`bg-zinc-900/60 rounded-xl border border-zinc-800/50 p-5 ${onClick ? 'cursor-pointer hover:border-zinc-700/50' : ''} transition-colors`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-zinc-500 text-xs font-medium">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColors[color]}`}>{icon}</div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};

// Quick Action Button
const QuickAction = ({ label, icon, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors text-sm text-left">
    {icon}
    <span className="flex-1">{label}</span>
    <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
  </button>
);

// Icon Button
const IconBtn = ({ icon, color, title, onClick }) => (
  <button onClick={onClick} className={`p-1.5 rounded-md ${color} hover:bg-zinc-800/50 transition-colors`} title={title}>{icon}</button>
);

export default AdminPage;
